import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [supportDetails, setSupportDetails] = useState({
    hasServiceWorker: false,
    hasPushManager: false,
    hasNotification: false,
    isSecureContext: false,
    isStandalone: false,
    userAgent: '',
  });
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [priorityOnly, setPriorityOnly] = useState(false);
  const [permission, setPermission] = useState('default');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if push notifications are supported
  useEffect(() => {
    const hasServiceWorker = 'serviceWorker' in navigator;
    const hasPushManager = 'PushManager' in window;
    const hasNotification = 'Notification' in window;
    const isSecureContext = window.isSecureContext === true;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    const userAgent = navigator.userAgent || '';

    setSupportDetails({
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      isSecureContext,
      isStandalone,
      userAgent,
    });

    const supported = hasServiceWorker && hasPushManager && hasNotification && isSecureContext;
    setIsSupported(supported);

    // Detailed diagnostic log — helps identify iOS/Safari edge cases
    console.log('[TRADEX Push Diagnostic]', {
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      isSecureContext,
      isStandalone,
      userAgent,
      supported,
      url: window.location.href,
    });

    if (hasNotification) {
      try {
        setPermission(Notification.permission);
        console.log('[TRADEX Push] Notification.permission =', Notification.permission);
      } catch (e) {
        console.warn('[TRADEX Push] Cannot read Notification.permission:', e);
      }
    }

    setLoading(false);
  }, []);

  // Check subscription status from backend
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return; // Not logged in
      
      const response = await axios.get(`${API}/push/status`, {
        headers: getAuthHeaders()
      });
      setIsSubscribed(response.data.subscribed);
      setPriorityOnly(response.data.priority_only);
    } catch (err) {
      console.error('Failed to check push status:', err);
    }
  }, []);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  // Register service worker
  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (err) {
      console.error('Service Worker registration failed:', err);
      throw err;
    }
  };

  // Subscribe to push notifications
  const subscribe = useCallback(async (priorityOnlyOption = false) => {
    if (!isSupported) {
      setError('Push notifications are not supported');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      // Request notification permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      
      if (perm !== 'granted') {
        setError('Notification permission denied');
        setLoading(false);
        return false;
      }

      // Register service worker
      const registration = await registerServiceWorker();
      await navigator.serviceWorker.ready;

      // Get VAPID public key from backend
      const vapidResponse = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = vapidResponse.data.public_key;

      if (!vapidPublicKey) {
        setError('VAPID key not configured on server');
        setLoading(false);
        return false;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to backend with auth token
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON(),
        priority_only: priorityOnlyOption
      }, {
        headers: getAuthHeaders()
      });

      setIsSubscribed(true);
      setPriorityOnly(priorityOnlyOption);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Push subscription failed:', err);
      setError(err.message || 'Failed to subscribe');
      setLoading(false);
      return false;
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Unsubscribe from browser
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Unsubscribe from backend
      await axios.delete(`${API}/push/unsubscribe`, {
        headers: getAuthHeaders()
      });

      setIsSubscribed(false);
      setLoading(false);
      return true;
    } catch (err) {
      console.error('Push unsubscription failed:', err);
      setError(err.message || 'Failed to unsubscribe');
      setLoading(false);
      return false;
    }
  }, []);

  // Update priority preference
  const updatePriorityOnly = useCallback(async (newPriorityOnly) => {
    try {
      await axios.put(`${API}/push/preferences?priority_only=${newPriorityOnly}`, {}, {
        headers: getAuthHeaders()
      });
      setPriorityOnly(newPriorityOnly);
      return true;
    } catch (err) {
      console.error('Failed to update preferences:', err);
      return false;
    }
  }, []);

  return {
    isSupported,
    supportDetails,
    isSubscribed,
    priorityOnly,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    updatePriorityOnly,
    checkSubscriptionStatus
  };
}

export default usePushNotifications;
